import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ProductCategory } from '../../types';
import { CategoryMenuConfig } from '../../themes/themeTypes';
import { cn } from '../../utils/cn';

interface CategoryMenuDropdownProps {
  categories: ProductCategory[];
  config: CategoryMenuConfig;
  className?: string;
  navLinkClassName?: string;
}

const CategoryMenuDropdown: React.FC<CategoryMenuDropdownProps> = ({ categories, config, className, navLinkClassName }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  const handleToggle = useCallback((index: number, hasChildren: boolean) => {
    if (!hasChildren) return;
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  if (!categories.length) return null;

  return (
    <div ref={menuRef} className={cn('flex items-center gap-6', className)} data-testid="category-menu-dropdown">
      {categories.map((category, index) => {
        const children = (category.category_children || [])
          .filter((c) => c.is_active && !c.is_internal)
          .sort((a, b) => a.rank - b.rank);
        const hasChildren = children.length > 0;
        const isOpen = openIndex === index;

        return (
          <div
            key={category.id}
            className="relative"
            onMouseEnter={() => {
              clearCloseTimer();
              if (hasChildren) setOpenIndex(index);
            }}
            onMouseLeave={() => {
              clearCloseTimer();
              closeTimerRef.current = setTimeout(() => setOpenIndex(null), 120);
            }}
          >
            <a
              href={`/categories/${category.handle}`}
              className={cn(
                'text-sm text-text-primary no-underline transition-all hover:text-primary whitespace-nowrap flex items-center gap-1 border-b-2 border-transparent hover:border-primary pb-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                navLinkClassName
              )}
              onClick={(e) => {
                if (hasChildren && !isOpen) {
                  e.preventDefault();
                  handleToggle(index, hasChildren);
                }
              }}
              data-testid="category-nav-link"
            >
              {category.name}
              {hasChildren && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              )}
            </a>

            {hasChildren && isOpen && (
              <>
              {/* Invisible bridge to prevent hover gap */}
              <div className="absolute top-full left-0 w-full h-3" />
              <div className="absolute top-full left-0 mt-2 bg-[var(--color-bg)] border border-border rounded-sm shadow-md min-w-[200px] py-2 z-50" data-testid="category-dropdown-panel">
                <a
                  href={`/categories/${category.handle}`}
                  className="block px-4 py-2 text-sm text-text-primary no-underline font-medium hover:bg-bg-secondary transition-colors"
                >
                  All {category.name}
                </a>
                <div className="border-t border-border my-1" />
                {children.map((child) => (
                  <a
                    key={child.id}
                    href={`/categories/${child.handle}`}
                    className="block px-4 py-2 text-sm text-text-secondary no-underline hover:bg-bg-secondary hover:text-primary transition-colors"
                    data-testid="category-dropdown-child-link"
                  >
                    {child.name}
                    {config.showDescriptions && child.description && (
                      <span className="block text-xs text-text-light mt-0.5 line-clamp-1">
                        {child.description}
                      </span>
                    )}
                  </a>
                ))}
              </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryMenuDropdown;
